import { useMemo } from 'react'

import { useExternalFinancialAccounts } from '@repo/common/queries/admin/external_financial'
import { useGetAllWalletBalances } from '@repo/common/queries/labs/blockchain/useGetAllWalletBalances'

import { type ExchangeSymbol } from '@/hooks/useExchangeRates.ts'

const EtherscanSepoliaUrl = 'https://sepolia.etherscan.io/address/'

export type AccountProvider = string // 'fireblocks' | 'external_financial' | 'zero'

export type BaseBalanceLookup = {
  lookup: 'eth' | 'feeder_master_shares' | 'external_financial' | 'zero'
  name: string
  provider?: AccountProvider
}

export interface EthBalanceLookup extends BaseBalanceLookup {
  lookup: 'eth'
  network: 'sepolia' // | 'mainnet'
  blockchainAddress: CryptoAddress
}

export interface FeederMasterSharesLookup extends BaseBalanceLookup {
  lookup: 'feeder_master_shares'
  feeder_symbol: string
}

export interface ExternalFinancialLookup extends BaseBalanceLookup {
  lookup: 'external_financial'
  id:
    | 'foundation-tenet-usd'
    | 'xvc-master-tenet-usd'
    | 'labs-chase-usd'
    | 'labs-feeder-usd' // prob migrate to string
}

export interface ZeroBalanceLookup extends BaseBalanceLookup {
  lookup: 'zero'
}

export type BalanceLookup =
  | EthBalanceLookup
  | FeederMasterSharesLookup
  | ExternalFinancialLookup
  | ZeroBalanceLookup

// export type EntityLookup = {
//   name: string
//   description: string
//   accounts: AccountLookup[]
// }

export type Entity = {
  id: string
  name: string
  description: string
}

export type AccountLookup = {
  name: string
  description?: string
  entityId: string
  external?: boolean
  balances: BalanceLookup[]
}

export interface AccountLeaf extends AccountNodeBase {
  type: 'leaf'
  assetSymbol: string
  balance: number
  usdEquivalent?: number
}

export interface AccountContainer extends AccountNodeBase {
  type: 'container'
  children: AccountNode[]
  external: boolean // todo remove external
  description?: string
  provider?: AccountProvider
  lastUpdated?: Date | string
  detailsUrl?: string

  // calculated
  assetSums?: Record<string, number>
  totalUsdEquivalent?: number
  empty: boolean
}

export interface AccountNodeBase {
  type: 'leaf' | 'container'
  id: string
  name: string
}

export type AccountLookupsProps = {
  lookups: AccountLookup[]
  entities?: Entity[]
  fundId?: string
  refetchInterval?: number
}

export type AccountNode = AccountLeaf | AccountContainer

const formatAddress = (address: string): string => {
  if (!address) return ''
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
}

function updateSums(node: AccountNode): void {
  if (node.type == 'leaf') {
    return
  }

  node.empty = true

  const addSumTo = (to: AccountContainer, symbol: string, sum: number) => {
    if (!to.assetSums) {
      to.assetSums = {}
    }
    if (!to.assetSums[symbol]) {
      to.assetSums[symbol] = 0
    }
    to.assetSums[symbol] += sum
  }

  node.children.forEach((child) => {
    if (child.type === 'container') {
      updateSums(child)
      node.empty = node.empty && child.empty

      // Only include non-external child containers in parent sums
      if (child.assetSums && !child.external) {
        Object.entries(child.assetSums).forEach(([symbol, sum]) => {
          addSumTo(node, symbol, sum)
        })
      }

      if (!child.empty && !child.external) {
        node.totalUsdEquivalent =
          (typeof node.totalUsdEquivalent == 'number' &&
            typeof child.totalUsdEquivalent == 'number' &&
            node.totalUsdEquivalent + child.totalUsdEquivalent) ||
          undefined // degrade to undefined if any entry is missing usd equivalency
      }
    } else {
      addSumTo(node, child.assetSymbol, child.balance)

      if (child.balance != 0) {
        node.empty = false
        node.totalUsdEquivalent =
          (typeof node.totalUsdEquivalent == 'number' &&
            typeof child.usdEquivalent == 'number' &&
            node.totalUsdEquivalent + child.usdEquivalent) ||
          undefined // degrade to undefined if any entry is missing usd equivalency
      }
    }
  })
}

export function useAccountLookups({
  lookups,

  fundId = '1',
  refetchInterval,
  entities,
}: AccountLookupsProps) {
  const addresses = useMemo(
    () =>
      lookups
        .flatMap((entity) => entity.balances)
        .filter(
          (lookup) =>
            lookup.lookup === 'eth' &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            lookup.network === 'sepolia' &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            !!lookup.blockchainAddress,
        )
        .map(
          (lookup) =>
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (lookup.lookup === 'eth' && lookup.blockchainAddress) || null,
        )
        .filter((x) => !!x),
    [lookups],
  )
  const externalFinancialIds = useMemo(
    () =>
      lookups
        .flatMap((entity) => entity.balances)
        .filter((lookup) => lookup.lookup === 'external_financial')
        .map(
          (lookup) =>
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (lookup.lookup === 'external_financial' && lookup.id) || null,
        )
        .filter((x) => !!x),
    [lookups],
  )

  const {
    balances: walletBalances,
    isLoading: walletBalancesLoading,
    isError,
    isFetching: isWalletBalancesFetching,
    error,
  } = useGetAllWalletBalances({ addresses, refetchInterval })
  // const {
  //   data: fundNavsData,
  //   isLoading: fundNavsLoading,
  //   isFetching: fundNavsFetching,
  // } = useAdminFundDailyNavs({
  //   fundId: fundId,
  //   count: 1,
  //   // todo refetchInterval
  // })
  if (fundId) {
    // temporarily disable fund navs
  }
  const {
    data: externalFinancialAccounts,
    isLoading: externalFinancialLoading,
    isFetching: isExternalFinancialFetching,
  } = useExternalFinancialAccounts({
    ids: externalFinancialIds.length > 0 ? externalFinancialIds : undefined,
    refetchInterval,
  })

  const isLoading: boolean =
    walletBalancesLoading ||
    // fundNavsLoading ||
    externalFinancialLoading
  const isFetching: boolean =
    isLoading ||
    isWalletBalancesFetching ||
    // fundNavsFetching ||
    isExternalFinancialFetching
  const accountNodes: AccountNode[] = useMemo((): AccountNode[] => {
    if (isLoading) {
      return []
    }

    // Group lookups by entityId
    const lookupsByEntity = lookups.reduce<Record<string, AccountLookup[]>>(
      (acc, lookup) => {
        if (!acc[lookup.entityId]) {
          acc[lookup.entityId] = []
        }
        acc[lookup.entityId]!.push(lookup)
        return acc
      },
      {},
    )

    const nodes = (entities || []).map((entity) => {
      const entityLookups = lookupsByEntity[entity.id] || []

      const entityNode: AccountContainer = {
        id: entity.id,
        type: 'container',
        name: entity.name,
        description: entity.description,
        external: false,
        totalUsdEquivalent: 0,
        children: [],
        empty: true,
      }

      entityNode.children = entityLookups.map((lookup) => {
        const node: AccountContainer = {
          id: `${entity.id}/${lookup.name}`,
          type: 'container',
          name: lookup.name,
          description: lookup.description,
          external: !!lookup.external,
          totalUsdEquivalent: 0,
          children: [],
          empty: true,
        }

        node.children = lookup.balances.map((balanceLookup) => {
          const balanceNode: AccountContainer = {
            id: `${entity.id}/${lookup.name}/${balanceLookup.name}`,
            type: 'container',
            name: balanceLookup.name,
            external: false,
            totalUsdEquivalent: 0,
            children: [],
            empty: true,
            provider: balanceLookup.provider,
          }

          const handleEth = () => {
            if (balanceLookup.lookup != 'eth') return
            const foundBalance = walletBalances.find(
              (balance) => balanceLookup.blockchainAddress === balance.address,
            )
            // todo mainnet
            const addSepoliaBalance = (symbol: ExchangeSymbol, bal: number) => {
              const usdEquivalent = 0
              // TODO use NAV ruby prices
              balanceNode.children.push({
                type: 'leaf',
                id: `${entity.id}/${lookup.name}/${balanceLookup.network}/${symbol}`,
                name: '', // formatAddress(balanceLookup.blockchainAddress),
                assetSymbol: symbol,
                balance: bal,
                usdEquivalent:
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  usdEquivalent > 0
                    ? parseFloat(usdEquivalent.toFixed(2))
                    : undefined,
              })
            }

            balanceNode.lastUpdated = new Date().toISOString()
            balanceNode.detailsUrl = `${EtherscanSepoliaUrl}${balanceLookup.blockchainAddress}`
            balanceNode.provider = `${balanceLookup.provider} ${formatAddress(balanceLookup.blockchainAddress)}`

            addSepoliaBalance('ETH', foundBalance?.eth || 0)

            foundBalance?.tokens.forEach((tokenBalance) => {
              addSepoliaBalance(
                tokenBalance.symbol as ExchangeSymbol,
                tokenBalance.balance,
              )
            })
          }

          const handleFeederMasterShares = () => {
            if (balanceLookup.lookup != 'feeder_master_shares') return
            // const feeder = fundNavsData?.feeders.find(
            //   (f) => f.symbol === balanceLookup.feeder_symbol,
            // )
            // if (!feeder || !feeder.daily_navs.nodes.length) return
            //
            // const latestNav = feeder.daily_navs.nodes[0]!
            // const masterShares: number = latestNav.owned_master_shares || 0
            // const pps = latestNav.price_per_share || 0
            // const usdValue = masterShares * pps

            balanceNode.children.push({
              type: 'leaf',
              id: `${entity.id}/${lookup.name}/fund/${balanceLookup.feeder_symbol}`,
              name: balanceLookup.name,
              assetSymbol: 'Shares',
              balance: 0, // masterShares,
              usdEquivalent: 0, // parseFloat(usdValue.toFixed(2)),
            })

            // balanceNode.lastUpdated = latestNav.date
          }

          const handleZero = () => {
            if (balanceLookup.lookup != 'zero') return
            balanceNode.children.push({
              type: 'leaf',
              id: `${entity.id}/${lookup.name}/zero`,
              name: balanceLookup.name,
              assetSymbol: '',
              balance: 0,
              usdEquivalent: 0,
            })

            balanceNode.lastUpdated = new Date().toISOString()
          }

          const handleExternalFinancial = () => {
            if (balanceLookup.lookup != 'external_financial') return
            const foundAccount = externalFinancialAccounts?.find(
              (account) => account.id === balanceLookup.id,
            )
            if (!foundAccount) return

            balanceNode.children.push({
              type: 'leaf',
              id: `${entity.id}/${lookup.name}/external_financial/${foundAccount.id}`,
              name: foundAccount.name,
              assetSymbol: foundAccount.currency,
              balance: foundAccount.current_balance,
              usdEquivalent:
                foundAccount.currency === 'USD'
                  ? foundAccount.current_balance
                  : undefined,
            })

            balanceNode.lastUpdated = foundAccount.updated_at
            balanceNode.provider = foundAccount.custodian
            balanceNode.detailsUrl = `/external-financial-account/${foundAccount.id}`
          }

          switch (balanceLookup.lookup) {
            case 'eth':
              handleEth()
              break
            case 'feeder_master_shares':
              handleFeederMasterShares()
              break
            case 'zero':
              handleZero()
              break
            case 'external_financial':
              handleExternalFinancial()
              break
          }
          return balanceNode
        })
        return node
      })
      return entityNode
    })

    nodes.forEach(updateSums)

    return nodes
  }, [
    isLoading,
    walletBalances,
    lookups,
    // fundNavsData?.feeders,
    entities,
    externalFinancialAccounts,
  ])

  return {
    accountNodes: accountNodes,
    isLoading,
    isError,
    error,
    walletBalances,
    isFetching,
  }
}
